import React, { useEffect, useState } from 'react';
import { legacyFincetClientsApi } from '../services/legacyFincetClientsApi';
import { ExternalStatus, STATUS_OPTIONS } from '../types/types';

// Type d'un client legacy
export interface LegacyClientData {
	legacyId: number;
	label: string;
	externalKey?: string;
	externalStatus: ExternalStatus;
	corelysId?: number;
}

interface Props {
	token: string;
}

export default function LegacyClientsTable({ token }: Props) {
	const [clients, setClients] = useState<LegacyClientData[]>([]);
	const [loading, setLoading] = useState(true);

	// Chargement des clients depuis l'API
	useEffect(() => {
		legacyFincetClientsApi
			.list(token)
			.then(setClients)
			.finally(() => setLoading(false));
	}, [token]);

	// Changer le status dans le state
	const handleStatusChange = (index: number, newStatus: ExternalStatus) => {
		const copy = [...clients];
		copy[index].externalStatus = newStatus;
		setClients(copy);
	};

	// Actions
	const handleImport = (legacyId: number) =>
		legacyFincetClientsApi
			.import(legacyId, token)
			.then((data) => {
				if (data.corelysId) {
					alert(`Importé avec succès, Corelys ID: ${data.corelysId}`);
					// Met à jour le state
					setClients((prev) =>
						prev.map((c) =>
							c.legacyId === legacyId
								? {
										...c,
										externalKey: `${data.corelysId}`,
										externalStatus: 'IMPORTED',
									}
								: c
						)
					);
				}
			})
			.catch((err) => alert(`Erreur réseau: ${err}`));

	const handleAssociate = (legacyId: number) => {
		const corelysId = prompt('ID Corelys ?');
		if (corelysId) {
			legacyFincetClientsApi
				.update(legacyId, { externalKey: `${corelysId}` }, token)
				.then(() => {
					alert(`Associé à ${corelysId}`);
					// Met à jour le state
					setClients((prev) =>
						prev.map((c) =>
							c.legacyId === legacyId
								? {
										...c,
										externalKey: `${corelysId}`,
										externalStatus: 'MANUALLY_LINKED',
									}
								: c
						)
					);
				});
		}
	};

	if (loading) return <p>Chargement...</p>;

	return (
		<table className="w-full border border-gray-300">
			<thead className="bg-gray-100">
				<tr>
					<th className="p-2 border">Label</th>
					<th className="p-2 border">Legacy Key</th>
					<th className="p-2 border">Local Key</th>
					<th className="p-2 border">Status</th>
					<th className="p-2 border">Actions</th>
				</tr>
			</thead>
			<tbody>
				{clients
					.filter((c) => c.label) // filtre les clients sans label
					.sort((a, b) => a.label.localeCompare(b.label)) // trie par label
					.map((c, idx) => {
						let rowClass = 'hover:bg-gray-50'; // classe par défaut
						if (
							c.externalStatus === 'IMPORTED' ||
							c.externalStatus === 'MANUALLY_LINKED'
						)
							rowClass += ' bg-green-100';
						else if (c.externalStatus === 'NOT_IMPORTED')
							rowClass += ' bg-yellow-100';
						else if (c.externalStatus === 'IGNORED') rowClass += ' bg-red-100';

						return (
							<tr key={c.legacyId} className={rowClass}>
								<td className="p-2 border">{c.label}</td>
								<td className="p-2 border">{c.legacyId}</td>
								<td className="p-2 border">
									{c.externalKey ? (
										<a
											href={`/app/tiers/${c.externalKey}/manage`}
											className="text-blue-600 underline"
										>
											{c.externalKey}
										</a>
									) : (
										'-'
									)}
								</td>
								<td className="p-2 border">
									<select
										value={c.externalStatus}
										onChange={(e) =>
											handleStatusChange(idx, e.target.value as ExternalStatus)
										}
									>
										{STATUS_OPTIONS.map((s) => (
											<option key={s} value={s}>
												{s}
											</option>
										))}
									</select>
								</td>
								<td className="p-2 border flex gap-2">
									{(c.externalStatus === 'NOT_IMPORTED' ||
										c.externalStatus === 'IGNORED') && (
										<>
											<button onClick={() => handleImport(c.legacyId)}>
												Importer
											</button>
											<button onClick={() => handleAssociate(c.legacyId)}>
												Associer
											</button>
										</>
									)}
								</td>
							</tr>
						);
					})}
			</tbody>
		</table>
	);
}
